<template>
  <div>
    <div class="w-full">
      <div class="mt-4 text-center">
        <div class="flex items-center justify-between gap-4 px-2 text-gray-900">
          <div class="flex flex-grow items-center justify-between">
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              :disabled="isPreviousDateDisabled"
              @click="subtractYear"
            >
              <span class="sr-only">Previous year</span>
              <ChevronLeftIcon
                class="relative -right-3.5 h-5 w-5"
                aria-hidden="true"
              />
              <ChevronLeftIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              :disabled="isPreviousDateDisabled"
              @click="subtractMonth"
            >
              <span class="sr-only">Previous month</span>
              <ChevronLeftIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
          </div>
          <div class="font-normal">{{ viewedMonthText }} {{ viewedYear }}</div>
          <div class="flex flex-grow items-center justify-between">
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              @click="addMonth"
            >
              <span class="sr-only">Next month</span>
              <ChevronRightIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              @click="addYear"
            >
              <span class="sr-only">Next year</span>
              <ChevronRightIcon
                class="h-5 w-5"
                aria-hidden="true"
              />
              <ChevronRightIcon
                class="relative -left-3.5 h-5 w-5"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
        <div class="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
          <div>S</div>
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
        </div>
        <div
          class="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
        >
          <button
            v-for="(day, dayIdx) in days"
            :key="day.date"
            type="button"
            :class="[
              'py-1.5 hover:bg-gray-100 focus:z-10',
              day.isCurrentMonth ? 'bg-white' : 'bg-gray-50',
              day.isSelected && 'text-white',
              !day.isSelected &&
                day.isCurrentMonth &&
                !day.isToday &&
                'text-gray-900',
              !day.isSelected &&
                !day.isCurrentMonth &&
                !day.isToday &&
                'text-gray-400',
              day.isToday && !day.isSelected && 'text-yellow-20',
              dayIdx === 0 && 'rounded-tl-lg',
              dayIdx === 6 && 'rounded-tr-lg',
              dayIdx === days.length - 7 && 'rounded-bl-lg',
              dayIdx === days.length - 1 && 'rounded-br-lg',
            ]"
            @click="selectDay(day, dayIdx)"
          >
            <time
              :datetime="day.date"
              :class="[
                'mx-auto flex h-7 w-7 items-center justify-center rounded-md',
                day.isSelected && day.isToday && 'bg-yellow-500',
                day.isSelected && !day.isToday && 'bg-yellow-500',
              ]"
            >
              {{ day?.date.split('-').pop().replace(/^0/, '') }}
            </time>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/solid';
import { ref, toRef, computed, watch, onBeforeMount } from 'vue';
import dayjs from 'dayjs';
export default {
  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    field: String,
    initialDate: {
      type: String,
    },
    handleInput: Function,
    restrictPrevious: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const field = ref(props.field);
    const initialDateValue = toRef(props, 'initialDate');

    const viewedDate = ref(
      dayjs(initialDateValue.value).startOf('month').format('YYYY-MM-DD')
    );
    const viewedMonth = computed(() => {
      return dayjs(viewedDate.value).format('M');
    });
    const viewedMonthText = computed(() => {
      return dayjs(viewedDate.value).format('MMMM');
    });
    const viewedYear = computed(() => {
      return dayjs(viewedDate.value).format('YYYY');
    });
    const initialDateDay = computed(() => {
      return dayjs(initialDateValue.value).format('D');
    });
    const days = ref([
      { date: '2021-12-27' },
      { date: '2021-12-28' },
      { date: '2021-12-29' },
      { date: '2021-12-30' },
      { date: '2021-12-31' },
      { date: '2022-01-01', isCurrentMonth: true },
      { date: '2022-01-02', isCurrentMonth: true },
      { date: '2022-01-03', isCurrentMonth: true },
      { date: '2022-01-04', isCurrentMonth: true },
      { date: '2022-01-05', isCurrentMonth: true },
      { date: '2022-01-06', isCurrentMonth: true },
      { date: '2022-01-07', isCurrentMonth: true },
      { date: '2022-01-08', isCurrentMonth: true },
      { date: '2022-01-09', isCurrentMonth: true },
      { date: '2022-01-10', isCurrentMonth: true },
      { date: '2022-01-11', isCurrentMonth: true },
      { date: '2022-01-12', isCurrentMonth: true },
      { date: '2022-01-13', isCurrentMonth: true },
      { date: '2022-01-14', isCurrentMonth: true },
      { date: '2022-01-15', isCurrentMonth: true },
      { date: '2022-01-16', isCurrentMonth: true },
      { date: '2022-01-17', isCurrentMonth: true },
      { date: '2022-01-18', isCurrentMonth: true },
      { date: '2022-01-19', isCurrentMonth: true },
      { date: '2022-01-20', isCurrentMonth: true },
      { date: '2022-01-21', isCurrentMonth: true },
      { date: '2022-01-22', isCurrentMonth: true },
      { date: '2022-01-23', isCurrentMonth: true },
      { date: '2022-01-24', isCurrentMonth: true },
      { date: '2022-01-25', isCurrentMonth: true },
      { date: '2022-01-26', isCurrentMonth: true },
      { date: '2022-01-27', isCurrentMonth: true },
      { date: '2022-01-28', isCurrentMonth: true },
      { date: '2022-01-29', isCurrentMonth: true },
      { date: '2022-01-30', isCurrentMonth: true },
      { date: '2022-01-31', isCurrentMonth: true },
      { date: '2022-02-01' },
      { date: '2022-02-02' },
      { date: '2022-02-03' },
      { date: '2022-02-04' },
      { date: '2022-02-05' },
      { date: '2022-02-06' },
    ]);
    function constructMonth() {
      let currentStart = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).format('YYYY-MM-DD');
      let daysInMonth = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).daysInMonth();
      let dayOfWeekFirst = dayjs(
        viewedMonth.value + '/01/' + viewedYear.value
      ).format('d');
      let dayOfWeekLast = dayjs(
        viewedMonth.value + '/' + daysInMonth + '/' + viewedYear.value
      ).format('d');
      let dateLast = dayjs(
        viewedMonth.value + '/' + daysInMonth + '/' + viewedYear.value
      ).format('YYYY-MM-DD');
      // let dateLast = dayjs(viewedMonth.value+'/'+daysInMonth+'/'+viewedYear.value).format('YYYY-MM-DD');
      let calendarDays = [];
      for (let i = dayOfWeekFirst; i > 0; i--) {
        // days of the previous month that are in view
        const d = dayjs(currentStart).subtract(i, 'day');
        const currentDate = dayjs();
        const isBefore = d.isBefore(currentDate);

        calendarDays.push({
          date: dayjs(currentStart).subtract(i, 'day').format('YYYY-MM-DD'),
          ...(props.restrictPrevious && isBefore ? { isDisabled: true } : {}),
        });
      }
      if (initialDateDay.value == 1) {
        calendarDays.push({
          date: dayjs(currentStart).format('YYYY-MM-DD'),
          isCurrentMonth: true,
          isSelected: true,
        });
      } else {
        // first day of current month
        const d = dayjs(currentStart);
        const currentDate = dayjs();
        const isBefore = d.isBefore(currentDate);

        calendarDays.push({
          date: dayjs(currentStart).format('YYYY-MM-DD'),
          isCurrentMonth: true,
          ...(props.restrictPrevious && isBefore ? { isDisabled: true } : {}),
        });
      }
      for (let i = 1; i < daysInMonth; i++) {
        if (i + 1 == initialDateDay.value) {
          // current day
          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            isSelected: true,
          });
        } else {
          // the days of the current month, excluding 1 and current day
          const d = dayjs(currentStart).add(i, 'day');
          const currentDate = dayjs();
          const isBefore = d.isBefore(currentDate);

          calendarDays.push({
            date: dayjs(currentStart).add(i, 'day').format('YYYY-MM-DD'),
            isCurrentMonth: true,
            ...(props.restrictPrevious && isBefore ? { isDisabled: true } : {}),
          });
        }
      }
      for (let i = 1; i <= 6 - dayOfWeekLast; i++) {
        calendarDays.push({
          date: dayjs(dateLast).add(i, 'day').format('YYYY-MM-DD'),
        });
      }
      days.value = [];
      days.value = calendarDays;
    }
    const selectedDay = ref({
      date: null,
      index: null,
    });
    function selectDay(day, dayIdx) {
      if (day.isDisabled) {
        return;
      }

      if (selectedDay.value.index && days.value[selectedDay.value.index]) {
        days.value[selectedDay.value.index].isSelected = false;
        days.value[dayIdx].isSelected = true;
      } else {
        days.value.forEach(function (el) {
          el.isSelected = false;
        });
        days.value[dayIdx].isSelected = true;
      }
      selectedDay.value.index = dayIdx;
      selectedDay.value.date = days.value[dayIdx].date;
      let fieldValue = field.value;
      let dateSelected = selectedDay.value.date;
      props.handleInput(dateSelected);
      emit('daySelected', fieldValue, dateSelected);
    }

    onBeforeMount(() => {
      constructMonth();
    });

    watch(viewedDate, () => {
      constructMonth();
    });
    function addMonth() {
      viewedDate.value = dayjs(viewedDate.value).add(1, 'month');
    }
    function subtractMonth() {
      viewedDate.value = dayjs(viewedDate.value).subtract(1, 'month');
    }
    function addYear() {
      viewedDate.value = dayjs(viewedDate.value).add(1, 'year');
    }

    function subtractYear() {
      const nextDate = dayjs(viewedDate.value)
        .subtract(1, 'year')
        .startOf('month');
      const currentDate = dayjs().startOf('month');

      const isBefore = nextDate.isBefore(currentDate);

      if (isBefore) {
        viewedDate.value = currentDate;
      } else {
        viewedDate.value = dayjs(viewedDate.value).subtract(1, 'year');
      }
    }

    const isPreviousDateDisabled = computed(() => {
      if (props.restrictPrevious) {
        const currentMonth = dayjs().format('M');
        const currentYear = dayjs().format('YYYY');

        const isOlder =
          viewedMonth?.value <= currentMonth &&
          viewedYear?.value <= currentYear;
        return isOlder;
      } else {
        return false;
      }
    });

    return {
      days,
      selectedDay,
      selectDay,
      viewedMonth,
      viewedMonthText,
      viewedYear,
      initialDateValue,
      addMonth,
      subtractMonth,
      addYear,
      subtractYear,
      // daysInMonth,
      // calendarDays,
      initialDateDay,
      isPreviousDateDisabled,
    };
  },
};
</script>
